"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Shared layout + styling primitive for the action-button row on
 * JobEntity, CompanyEntity, and ContactEntity. Owns:
 *   - Responsive layout (desktop inline / tablet grid / mobile stack).
 *   - Button styling for primary / secondary / ghost variants.
 *   - Spacing, disabled, and paywall state.
 *
 * Do NOT use outside `entities/`. External call sites should consume
 * the entity presets or pass bespoke children into an entity via its
 * public props.
 */

type Layout = "auto" | "inline" | "grid" | "stack" | "row";

export interface EntityActionsProps {
  children: React.ReactNode;
  /**
   * auto  — let ResizeObserver pick inline / grid / stack based on width
   * inline — desktop-style single row, actions pinned right
   * grid  — two-column grid (mobile)
   * stack — full-width stacked buttons
   * row   — compact icon-only row (for table layout)
   */
  layout?: Layout;
  /** Minimum container width (px) before switching to `inline`. */
  inlineMinWidth?: number;
  /** Minimum container width (px) before switching from `stack` to `grid`. */
  gridMinWidth?: number;
  className?: string;
}

type ResolvedLayout = Exclude<Layout, "auto">;

export const EntityActions = React.forwardRef<HTMLDivElement, EntityActionsProps>(
  function EntityActions(
    {
      children,
      layout = "auto",
      inlineMinWidth = 520,
      gridMinWidth = 320,
      className,
    },
    forwardedRef,
  ) {
    const internalRef = React.useRef<HTMLDivElement | null>(null);
    const [resolved, setResolved] = React.useState<ResolvedLayout>(
      layout === "auto" ? "stack" : layout,
    );

    const setRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    React.useEffect(() => {
      if (layout !== "auto") {
        setResolved(layout);
        return;
      }
      const el = internalRef.current;
      if (!el || typeof ResizeObserver === "undefined") return;

      const update = (width: number) => {
        const next: ResolvedLayout =
          width >= inlineMinWidth
            ? "inline"
            : width >= gridMinWidth
              ? "grid"
              : "stack";
        setResolved((curr) => (curr === next ? curr : next));
      };

      update(el.getBoundingClientRect().width);
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        update(entry.contentRect.width);
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, [layout, inlineMinWidth, gridMinWidth]);

    return (
      <div
        ref={setRef}
        data-entity-actions-layout={resolved}
        className={cn(
          "flex gap-2",
          resolved === "inline" && "flex-row items-center justify-end",
          resolved === "grid" && "grid grid-cols-2",
          resolved === "stack" && "flex-col",
          resolved === "row" && "flex-row items-center",
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

/* --------------------------- EntityActionButton --------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost";
type ButtonSize = "sm" | "md";

export interface EntityActionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ComponentType<{ className?: string }>;
  iconPosition?: "start" | "end";
  /** When true, render as icon-only (square, no label shown). */
  iconOnly?: boolean;
  /**
   * When the entity is in paywall mode, a secondary greyed style is
   * applied and onClick is suppressed by the caller.
   */
  paywall?: boolean;
  children: React.ReactNode;
}

const BASE_CLASSES =
  "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap border font-bold uppercase tracking-[0.08em] transition-all duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "border-[var(--burgundy-core)] bg-[var(--burgundy-core)] text-white hover:bg-[var(--burgundy-core)]/90",
  secondary:
    "border-border bg-background text-foreground hover:bg-muted",
  ghost:
    "border-transparent bg-transparent text-foreground hover:bg-muted",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "min-h-8 px-2.5 py-1.5 text-[11px]",
  md: "min-h-9 px-3 py-2 text-xs",
};

const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
  sm: "h-8 w-8 p-0",
  md: "h-9 w-9 p-0",
};

export const EntityActionButton = React.forwardRef<
  HTMLButtonElement,
  EntityActionButtonProps
>(function EntityActionButton(
  {
    variant = "secondary",
    size = "md",
    icon: Icon,
    iconPosition = "start",
    iconOnly = false,
    paywall = false,
    className,
    children,
    style,
    ...rest
  },
  ref,
) {
  const boxShadow =
    variant === "ghost" ? undefined : "var(--shadow-warm-sm)";
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        BASE_CLASSES,
        VARIANT_CLASSES[variant],
        iconOnly ? ICON_ONLY_SIZE[size] : SIZE_CLASSES[size],
        paywall && "opacity-70",
        "w-full",
        className,
      )}
      style={{ boxShadow, ...style }}
      {...rest}
    >
      {Icon && iconPosition === "start" ? (
        <Icon className={iconOnly ? "h-4 w-4" : "h-3.5 w-3.5"} />
      ) : null}
      {!iconOnly && <span className="min-w-0 truncate">{children}</span>}
      {Icon && iconPosition === "end" && !iconOnly ? (
        <Icon className="h-3.5 w-3.5" />
      ) : null}
      {iconOnly && (
        <span className="sr-only">
          {typeof children === "string" ? children : "action"}
        </span>
      )}
    </button>
  );
});

/* ------------------------------ EntityMetaChip ----------------------------- */

export interface EntityMetaChipProps {
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  /** "solid" => filled muted chip; "subtle" => thin-border chip. */
  tone?: "solid" | "subtle";
  className?: string;
}

/**
 * Tactical chip used for inline metadata (location, date, category).
 * Provided here so all three entities render metadata identically.
 */
export function EntityMetaChip({
  icon: Icon,
  children,
  tone = "subtle",
  className,
}: EntityMetaChipProps) {
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 px-2 py-1 text-[11px] font-medium",
        tone === "subtle"
          ? "border border-border/60 bg-background text-foreground"
          : "border border-border bg-muted text-foreground",
        className,
      )}
    >
      {Icon ? (
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : null}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

/* --------------------------- EntityBadge (kicker) -------------------------- */

/**
 * Rendered on the leading edge of a meta row to flag a match context
 * (e.g., "Best Match", "Featured"). Uses the tactical filled-chip look.
 */
export function EntityBadge({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center border border-border bg-[var(--burgundy-core)] px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-white",
        className,
      )}
    >
      {children}
    </span>
  );
}
